import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { AccountDeviceFacade } from "@youm-front/account-device/data-access";
import {
  AudioInputDeviceDisabled,
  VideoInputDeviceDisabled,
  VideoInputDeviceIsFlipped,
  VideoInputResolution
} from "@youm-front/account-device/domain";
import { DeviceService } from "@youm-front/device/data-access";
import {
  AudioInputDeviceId,
  VideoInputDeviceId,
  VideoInputHeight,
  VideoInputWidth
} from "@youm-front/device/domain";
import { EMPTY, Observable, combineLatest, of } from "rxjs";
import { concatMap, map, take, tap } from "rxjs/operators";
import {
  CanvasedExtendedMediaStream,
  ExtendedMediaStream,
  PlainExtendedMediaStream
} from "youm-lib";

@Injectable()
export class AccountCamMicStreamFactoryService {
  constructor(
    private readonly accountDeviceFacade: AccountDeviceFacade,
    private readonly deviceService: DeviceService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  camStreamFactory = () => {
    return combineLatest([
      this.accountDeviceFacade.videoInputDeviceId$,
      this.accountDeviceFacade.videoInputResolution$
    ]).pipe(
      take(1),
      concatMap((_) => {
        const { videoInputHeight, videoInputWidth } =
          this.videoInputWidthHeight(_[1]);

        return _[0]
          ? this.deviceService.getUserMediaVideoInputStreamTrack$({
              deviceId: { exact: _[0] },
              width: { ideal: videoInputWidth },
              height: { ideal: videoInputHeight },
              facingMode: { ideal: "user" },
              frameRate: { ideal: 30 },
              aspectRatio: { exact: 16 / 9 }
            })
          : EMPTY;
      })
    );
  };

  micStreamFactory = () => {
    return this.accountDeviceFacade.audioInputDeviceId$.pipe(
      take(1),
      concatMap((_) =>
        _
          ? this.deviceService.getUserMediaAudioInputStreamTrack$({
              deviceId: { exact: _ }
            })
          : EMPTY
      )
    );
  };

  create({
    audioInputDeviceDisabled,
    audioInputDeviceId,
    videoInputDeviceDisabled,
    videoInputDeviceId,
    videoInputDeviceIsFlipped,
    videoInputResolution
  }: {
    audioInputDeviceDisabled: AudioInputDeviceDisabled;
    audioInputDeviceId: null | AudioInputDeviceId;
    videoInputDeviceDisabled: VideoInputDeviceDisabled;
    videoInputDeviceId: null | VideoInputDeviceId;
    videoInputDeviceIsFlipped: VideoInputDeviceIsFlipped;
    videoInputResolution: null | VideoInputResolution;
  }): Observable<ExtendedMediaStream> {
    if (!audioInputDeviceId && !videoInputDeviceId) {
      return of(
        this.buildExtendedMediaStream(
          new MediaStream(),
          videoInputDeviceIsFlipped
        )
      );
    }

    const { videoInputWidth, videoInputHeight } =
      this.videoInputWidthHeight(videoInputResolution);

    return this.deviceService
      .getUserMedia$({
        audio: audioInputDeviceId
          ? { deviceId: { exact: audioInputDeviceId } }
          : false,
        video: videoInputDeviceId
          ? {
              deviceId: { exact: videoInputDeviceId },
              width: { ideal: videoInputWidth },
              height: { ideal: videoInputHeight },
              facingMode: { ideal: "user" },
              frameRate: { ideal: 30 },
              aspectRatio: { exact: 16 / 9 }
            }
          : false
      })
      .pipe(
        map((_) => this.buildExtendedMediaStream(_, videoInputDeviceIsFlipped)),
        tap((_) => {
          if (videoInputDeviceDisabled) {
            _.videoDisable();
          }

          if (audioInputDeviceDisabled) {
            _.audioDisable();
          }
        })
      );
  }

  private videoInputWidthHeight(
    videoInputResolution: null | VideoInputResolution
  ): {
    videoInputWidth: VideoInputWidth;
    videoInputHeight: VideoInputHeight;
  } {
    switch (videoInputResolution) {
      case "1080p@30":
        return {
          videoInputWidth: 1920 as VideoInputWidth,
          videoInputHeight: 1080 as VideoInputHeight
        };
      case "480p@30":
        return {
          videoInputWidth: 854 as VideoInputWidth,
          videoInputHeight: 480 as VideoInputHeight
        };
      case "360p@30":
        return {
          videoInputWidth: 640 as VideoInputWidth,
          videoInputHeight: 360 as VideoInputHeight
        };
      case "240p@30":
        return {
          videoInputWidth: 426 as VideoInputWidth,
          videoInputHeight: 240 as VideoInputHeight
        };
      case "720p@30":
      default:
        return {
          videoInputWidth: 1280 as VideoInputWidth,
          videoInputHeight: 720 as VideoInputHeight
        };
    }
  }

  private buildExtendedMediaStream(
    stream: MediaStream,
    videoInputDeviceIsFlipped: VideoInputDeviceIsFlipped
  ) {
    const stream1 = new PlainExtendedMediaStream(stream);
    if (!videoInputDeviceIsFlipped) {
      return stream1;
    }

    return new CanvasedExtendedMediaStream(
      stream1,
      { toFlip: true },
      () => this.document.createElement("video"),
      () => this.document.createElement("canvas")
    );
  }
}
